import React from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { formatCurrency } from "../../../utils/formatCurrency";
import { PriceBreakdownTooltip } from "../../Pricing/PriceBreakdownTooltip";
import {
    AGENTIC_AI_COPY,
    RESULTS_STATS,
    WHY_EXPERT_LED,
    composePackageBlurb,
    composeWhyThisMix,
    contentTypeDuration,
    impactStarsForRank,
} from "../strategyCopy";
import { BLACK, BORDER, CARD_BG, FONT, GOLD, GOLD_DARK, GOLD_TINT, PAGE_BG, SURFACE_2, TEXT_FAINT, TEXT_MUTED } from "../theme";
import { PerContentTypeEstimate, ProposalExamplesResponse } from "../types";
import { VideoGrid } from "../../VideoGrid/VideoGrid";
import { StrategyData, StrategyUnit, unitCount } from "./StrategyData";

// The slide-3 strategy layout, shared by wizard step 3, the internal detail
// page, the public lead view — and mirrored (not reused) by the PDF.
// Left rail: your selections + why expert-led. Center: numbered video mix +
// results stat tiles. Right rail: why this mix works + package blurb.
interface ProposalStrategyViewProps {
    data: StrategyData;
    // Per-content-type prices + breakdown tooltips — internal viewers only.
    showPricingBreakdowns?: boolean;
    perContentTypeEstimates?: PerContentTypeEstimate[] | null;
    // Demo videos for the selected category — renders the "See it in
    // action" strip when present.
    examples?: ProposalExamplesResponse | null;
    // Suppress the greyed "Optional — in {next tier}" upsell rows (wizard
    // step 3 hides them; share page / detail page / PDF keep them).
    hideOptionalUnits?: boolean;
    footer?: React.ReactNode;
}

const sectionTitleStyle: React.CSSProperties = {
    fontFamily: FONT,
    fontSize: 16,
    fontWeight: 700,
    color: BLACK,
    margin: "0 0 10px",
};

const railCardStyle: React.CSSProperties = {
    fontFamily: FONT,
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    padding: "16px 14px",
};

function Stars({ count }: { count: number }) {
    return (
        <span style={{ color: GOLD, fontSize: 13, letterSpacing: 1 }} aria-label={`${count} of 5 impact`}>
            {"★".repeat(count)}
            <span style={{ color: BORDER }}>{"★".repeat(5 - count)}</span>
        </span>
    );
}

function MixCard({
    unit,
    index,
    optionalLabel,
    price,
}: {
    unit: StrategyUnit;
    index: number | null;
    optionalLabel?: string | null;
    price?: React.ReactNode;
}) {
    const duration = contentTypeDuration(unit.name);
    const optional = !!optionalLabel;
    return (
        <div
            style={{
                fontFamily: FONT,
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                background: optional ? PAGE_BG : CARD_BG,
                border: `1px solid ${BORDER}`,
                borderRadius: 12,
                padding: "13px 14px",
                opacity: optional ? 0.75 : 1,
            }}
        >
            <span
                style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: optional ? SURFACE_2 : GOLD_DARK,
                    color: optional ? TEXT_MUTED : "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginTop: 1,
                }}
            >
                {optional ? "+" : index}
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: BLACK }}>
                        {unit.quantity > 1 ? `${unit.quantity}× ` : ""}
                        {unit.displayName}
                    </span>
                    {duration && (
                        <span
                            style={{
                                fontSize: 10.5,
                                fontWeight: 700,
                                color: TEXT_MUTED,
                                background: SURFACE_2,
                                borderRadius: 99,
                                padding: "2px 8px",
                            }}
                        >
                            {duration}
                        </span>
                    )}
                    {optionalLabel && (
                        <span
                            style={{
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: 0.4,
                                color: GOLD_DARK,
                                background: GOLD_TINT,
                                borderRadius: 99,
                                padding: "2px 8px",
                                textTransform: "uppercase",
                            }}
                        >
                            {optionalLabel}
                        </span>
                    )}
                    <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {price}
                    </span>
                </div>
                {unit.reason && (
                    <div style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.5, marginTop: 3 }}>
                        {unit.reason}
                    </div>
                )}
                <div style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 600, color: TEXT_FAINT }}>Impact</span>
                    <Stars count={impactStarsForRank(unit.rank)} />
                </div>
            </div>
        </div>
    );
}

export function ProposalStrategyView({
    data,
    showPricingBreakdowns = false,
    perContentTypeEstimates,
    examples,
    hideOptionalUnits = false,
    footer,
}: ProposalStrategyViewProps) {
    const wide = useMediaQuery("(min-width:1100px)");
    const medium = useMediaQuery("(min-width:900px)");

    const whyMix = composeWhyThisMix(data.objectiveSlug, data.secondaryObjectiveSlug);
    const packageBlurb =
        data.objectiveName && data.objectiveSlug
            ? composePackageBlurb(
                data.tier,
                { name: data.objectiveName, slug: data.objectiveSlug },
                data.secondaryObjectiveName && data.secondaryObjectiveSlug
                    ? { name: data.secondaryObjectiveName, slug: data.secondaryObjectiveSlug }
                    : null,
            )
            : null;

    const estimateByContentType = new Map(
        (perContentTypeEstimates || []).map((e) => [e.content_type_id, e]),
    );

    const priceFor = (unit: StrategyUnit): React.ReactNode => {
        if (!showPricingBreakdowns) return null;
        const est = estimateByContentType.get(unit.contentTypeId);
        if (!est) return null;
        return (
            <span
                onClick={(e) => e.stopPropagation()}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 12,
                    color: TEXT_MUTED,
                    fontWeight: 600,
                }}
            >
                {est.quantity > 1
                    ? `${est.quantity} × ${formatCurrency(est.unit_total)}`
                    : formatCurrency(est.unit_total)}
                {est.breakdown && <PriceBreakdownTooltip breakdown={est.breakdown} />}
            </span>
        );
    };

    const selectionsRail = (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={railCardStyle}>
                <div
                    style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.6,
                        color: TEXT_FAINT,
                        textTransform: "uppercase",
                        marginBottom: 10,
                    }}
                >
                    Your selections
                </div>
                <div style={{ fontSize: 11.5, color: TEXT_FAINT, marginBottom: 2 }}>Primary goal</div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: BLACK, marginBottom: 10 }}>
                    {data.objectiveName || "—"}
                </div>
                {data.secondaryObjectiveName && (
                    <>
                        <div style={{ fontSize: 11.5, color: TEXT_FAINT, marginBottom: 2 }}>
                            Secondary goal
                        </div>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: BLACK, marginBottom: 10 }}>
                            {data.secondaryObjectiveName}
                        </div>
                    </>
                )}
                {data.categoryName && (
                    <>
                        <div style={{ fontSize: 11.5, color: TEXT_FAINT, marginBottom: 2 }}>Category</div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: BLACK, marginBottom: 10 }}>
                            {data.categoryName}
                        </div>
                    </>
                )}
                <div style={{ fontSize: 11.5, color: TEXT_FAINT, marginBottom: 4 }}>Where it lives</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {data.channels.map((ch) => (
                        <div
                            key={ch.id}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: 12.5,
                                color: BLACK,
                                fontWeight: 600,
                            }}
                        >
                            <span>{ch.name}</span>
                            {ch.quantity > 1 && (
                                <span style={{ color: TEXT_MUTED, fontWeight: 500 }}>
                                    ×{ch.quantity}
                                </span>
                            )}
                        </div>
                    ))}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 12.5,
                            color: GOLD_DARK,
                            fontWeight: 600,
                        }}
                    >
                        <span>✨ Agentic AI</span>
                        <span style={{ fontWeight: 500, fontSize: 11 }}>Included</span>
                    </div>
                </div>
            </div>
            <div style={{ ...railCardStyle, background: GOLD_TINT, border: `1px solid ${BORDER}` }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: BLACK, marginBottom: 4 }}>
                    ✨ Why expert-led video?
                </div>
                <div style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.55 }}>{WHY_EXPERT_LED}</div>
            </div>
        </div>
    );

    const mixColumn = (
        <div>
            <h3 style={sectionTitleStyle}>Your recommended video mix</h3>
            <p style={{ fontFamily: FONT, fontSize: 12.5, color: TEXT_MUTED, margin: "0 0 12px", lineHeight: 1.55 }}>
                These video types work together to {(data.objectiveName || "reach your goal").toLowerCase()} at
                every stage of the shopper journey.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {data.units.map((unit, i) => (
                    <MixCard key={unit.contentTypeId} unit={unit} index={i + 1} price={priceFor(unit)} />
                ))}
                {!hideOptionalUnits &&
                    data.nextTierUnits.map((unit) => (
                        <MixCard
                            key={`next-${unit.contentTypeId}`}
                            unit={unit}
                            index={null}
                            optionalLabel={`Optional — in ${data.nextTierLabel}`}
                        />
                    ))}
            </div>
        </div>
    );

    // Full-width band below the 3-column grid.
    const resultsRow = (
        <div style={{ marginTop: 20 }}>
            <h3 style={sectionTitleStyle}>How this drives results</h3>
            <p style={{ fontFamily: FONT, fontSize: 12.5, color: TEXT_MUTED, margin: "0 0 12px", lineHeight: 1.55 }}>
                Expected outcomes when this strategy is implemented across your selected placements.
            </p>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: medium ? "repeat(4, 1fr)" : "repeat(2, 1fr)",
                    gap: 10,
                }}
            >
                {RESULTS_STATS.map((s) => (
                    <div key={s.title} style={{ ...railCardStyle, padding: "14px 12px" }}>
                        <div style={{ fontSize: 17, fontWeight: 800, color: GOLD_DARK, marginBottom: 2 }}>
                            {s.stat}
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: BLACK, marginBottom: 3 }}>
                            {s.title}
                        </div>
                        <div style={{ fontSize: 10, fontStyle: "italic", color: TEXT_MUTED, marginBottom: 3 }}>
                            {s.source}
                        </div>
                        <div style={{ fontSize: 11, color: TEXT_MUTED, lineHeight: 1.45 }}>
                            {s.description}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const whyRail = (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={railCardStyle}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: BLACK, marginBottom: 2 }}>
                    🎯 Why this mix works
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: GOLD_DARK, marginBottom: 8 }}>
                    {whyMix.headline}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {whyMix.bullets.map((b, i) => (
                        <div key={i} style={{ display: "flex", gap: 7, alignItems: "flex-start" }}>
                            <span style={{ color: GOLD, fontSize: 11, marginTop: 2 }}>✓</span>
                            <span style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.5 }}>{b}</span>
                        </div>
                    ))}
                </div>
            </div>
            {packageBlurb && (
                <div style={railCardStyle}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: BLACK, marginBottom: 6 }}>
                        About this package
                    </div>
                    <div style={{ fontSize: 11.5, color: TEXT_MUTED, lineHeight: 1.6 }}>{packageBlurb}</div>
                </div>
            )}
            <div style={{ ...railCardStyle, background: "#1C1A17", border: "1px solid #1C1A17" }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "#fff", marginBottom: 4 }}>
                    ✨ {AGENTIC_AI_COPY.title}
                </div>
                <div style={{ fontSize: 11.5, color: "#B5AFA5", lineHeight: 1.55 }}>
                    {AGENTIC_AI_COPY.description}
                </div>
            </div>
        </div>
    );

    return (
        <div style={{ fontFamily: FONT }}>
            {/* Full width above the columns: what the analysis behind this
                strategy found (PDP, Consumer Voice or AI Presence). */}
            {data.description && (
                <div style={{ ...railCardStyle, background: GOLD_TINT, border: `1px solid ${BORDER}`, marginBottom: 16 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: BLACK, marginBottom: 6 }}>
                        📊 From your analysis
                    </div>
                    <div style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.6, whiteSpace: "pre-line" }}>
                        {data.description}
                    </div>
                </div>
            )}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: wide
                        ? "230px minmax(0, 1fr) 250px"
                        : medium
                            ? "230px minmax(0, 1fr)"
                            : "1fr",
                    gap: 16,
                    alignItems: "start",
                }}
            >
                {selectionsRail}
                {mixColumn}
                {wide ? whyRail : <div style={{ gridColumn: medium ? "1 / -1" : "auto" }}>{whyRail}</div>}
            </div>
            {resultsRow}
            {examples && examples.videos.length > 0 && (
                <div style={{ marginTop: 20 }}>
                    <h3 style={sectionTitleStyle}>See it in action</h3>
                    <p style={{ fontFamily: FONT, fontSize: 12.5, color: TEXT_MUTED, margin: "0 0 12px", lineHeight: 1.55 }}>
                        Real expert videos we've produced for {data.categoryName ? `${data.categoryName.toLowerCase()} ` : ""}
                        products like yours.
                    </p>
                    <VideoGrid
                        videos={examples.videos.slice(0, 6).map((v) => ({
                            id: v.id,
                            title: v.content_type ? v.content_type.display_name : v.title,
                            desc: [v.brand_name, v.product_title].filter(Boolean).join(" · "),
                            duration: v.length ? `${Math.round(v.length)}s` : undefined,
                            src: v.location,
                        }))}
                    />
                </div>
            )}
            {examples && examples.experts && examples.experts.length > 0 && (
                <div style={{ marginTop: 20 }}>
                    <h3 style={sectionTitleStyle}>Industry experts for your category</h3>
                    <p style={{ fontFamily: FONT, fontSize: 12.5, color: TEXT_MUTED, margin: "0 0 12px", lineHeight: 1.55 }}>
                        Vetted experts we'd suggest for {data.categoryName ? `${data.categoryName.toLowerCase()} ` : "your "}
                        videos like yours.
                    </p>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: medium ? "repeat(3, minmax(0, 1fr))" : "1fr",
                            gap: 10,
                        }}
                    >
                        {examples.experts.slice(0, 3).map((ex) => (
                            <div
                                key={ex.userid}
                                style={{ ...railCardStyle, display: "flex", gap: 12, alignItems: "flex-start" }}
                            >
                                {ex.avatar ? (
                                    <img
                                        src={ex.avatar}
                                        alt={ex.display_name}
                                        style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                                    />
                                ) : (
                                    <div
                                        style={{ width: 44, height: 44, borderRadius: "50%", background: GOLD_TINT, flexShrink: 0 }}
                                    />
                                )}
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontSize: 13.5, fontWeight: 700, color: BLACK }}>
                                        {ex.display_name}
                                    </div>
                                    {ex.credential && (
                                        <div style={{ fontSize: 11.5, fontWeight: 600, color: GOLD_DARK, marginTop: 1 }}>
                                            {ex.credential}
                                        </div>
                                    )}
                                    {ex.industry_years && (
                                        <div style={{ fontSize: 11, color: TEXT_FAINT, marginTop: 1 }}>
                                            {ex.industry_years}
                                        </div>
                                    )}
                                    {(ex.relevance || ex.blurb) && (
                                        <div style={{ fontSize: 11.5, color: TEXT_MUTED, lineHeight: 1.5, marginTop: 5 }}>
                                            {ex.relevance || ex.blurb}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {footer && <div style={{ marginTop: 18 }}>{footer}</div>}
        </div>
    );
}
